import React from 'react';
import useI18n from '@/hooks/useI18n';
import {
  formatCompactNumber,
  formatMoney,
  formatPercent,
} from '@/utils/numberUtils';
import { barPercent } from '@/utils/campaignMetrics';
import type { CampaignKpiBenchmarks } from '@/interfaces/campaignKpis';
import ToolTip from '@/components/common/ToolTip';
import { InfoIcon } from '@/svg';

const EM_DASH = '—';

/** Bar fills, sampled from the populated Figma frame. */
const FILL = {
  campaign: 'bg-[#635BFF]',
  reward: 'bg-[#15803D]',
  benchmark: 'bg-[#E4E4E6]',
} as const;

type RowTone = keyof typeof FILL;

/**
 * The 8px comparison bar under each legend row, drawn as a bare fill like the
 * Figma frame. Only an empty row shows an outlined track, so a zero still
 * reads as a bar rather than a gap.
 */
const ComparisonBar: React.FC<{ percent: number; tone: RowTone }> = ({
  percent,
  tone,
}) =>
  percent > 0 ? (
    <div
      data-testid="bar-fill"
      className={`h-[8px] rounded-[5px] ${FILL[tone]}`}
      style={{ width: `${percent}%` }}
    />
  ) : (
    <div className="h-[8px] w-full rounded-[5px] border border-[#E2E8F0]" />
  );

const LegendRow: React.FC<{
  label: string;
  value: string;
  percent?: number;
  tone?: RowTone;
}> = ({ label, value, percent = 0, tone = 'campaign' }) => (
  <div data-testid="legend-row" className="flex w-full flex-col gap-1.5">
    <div className="flex w-full items-center justify-between gap-2">
      <p className="font-header text-sm font-normal text-[#71717A]">{label}</p>
      {/* A real figure reads at full strength; an em dash stays muted. */}
      <p
        data-testid="legend-value"
        className={`font-header font-semibold ${
          tone === 'benchmark' ? 'text-[15px] leading-5' : 'text-sm'
        } ${value === EM_DASH ? 'text-[#18181C]/[0.31]' : 'text-[#18181C]'}`}
      >
        {value}
      </p>
    </div>
    <ComparisonBar percent={percent} tone={tone} />
  </div>
);

const KpiCard: React.FC<{
  testId: string;
  label: string;
  /** Shown on hovering an info icon beside the label. */
  labelTooltip?: string;
  metric: string;
  badge?: string;
  badgeTone?: 'neutral' | 'positive';
  subtext: string;
  subtextClassName?: string;
  children: React.ReactNode;
}> = ({
  testId,
  label,
  labelTooltip,
  metric,
  badge,
  badgeTone = 'neutral',
  subtext,
  subtextClassName = '',
  children,
}) => (
  <div
    data-testid={testId}
    className="flex min-w-0 flex-1 flex-col gap-6 self-stretch rounded-3xl border border-[#E2E8F0] bg-white p-5"
  >
    <div className="flex w-full flex-col gap-2">
      <div className="flex items-center gap-1">
        <p className="font-header text-sm font-semibold text-[#2A2929]">
          {label}
        </p>
        {labelTooltip && (
          <ToolTip
            text={labelTooltip}
            position="top"
            className="tooltip-nowrap"
          >
            <InfoIcon
              data-testid="kpi-info"
              className="size-4 cursor-pointer text-[#2A2929]"
            />
          </ToolTip>
        )}
      </div>
      <div className="flex items-center gap-3">
        <p
          data-testid="kpi-metric"
          className="font-header text-2xl font-normal leading-10 text-[#191B1C]"
        >
          {metric}
        </p>
        {badge && (
          <span
            data-testid="kpi-badge"
            className={`rounded-md px-2 py-1 font-header text-sm font-bold ${
              badgeTone === 'positive'
                ? 'bg-[#DCFCE7] text-[#15803D]'
                : 'bg-[#E2E8F0] text-[#71717A]'
            }`}
          >
            {badge}
          </span>
        )}
      </div>
      <p
        data-testid="kpi-subtext"
        className={`font-header text-sm font-normal leading-4 text-[#9B919D] ${subtextClassName}`}
      >
        {subtext}
      </p>
    </div>
    <div className="flex w-full flex-col gap-3">{children}</div>
  </div>
);

interface CampaignKpiCardsProps {
  /** False while no creator has posted — metrics render as em dashes. */
  hasSubmissions: boolean;
  totalSales: number;
  totalSpent: number;
  /** Sales per unit spent, from the API. */
  roas: number;
  /** Likes + shares, the divisor behind the cost per engagement. */
  totalEngagements: number;
  /** Spend per like or share, from the API. Null means no engagement yet. */
  costPerEngagement: number | null;
  /** Whole % cheaper than the paid-social benchmark, or null. */
  cheaperPercent: number | null;
  engagementRate: number;
  benchmarks: CampaignKpiBenchmarks;
}

const CampaignKpiCards: React.FC<CampaignKpiCardsProps> = ({
  hasSubmissions,
  totalSales,
  totalSpent,
  roas,
  totalEngagements,
  costPerEngagement,
  cheaperPercent,
  engagementRate,
  benchmarks,
}) => {
  const { t } = useI18n();

  const { paidSocialCpe, metaAdsEngagementRate } = benchmarks;

  // Passing null to the formatters yields an em dash, which is what the design
  // shows before any submission lands.
  const salesMetric = formatMoney(hasSubmissions ? totalSales : null);
  const cpeMetric = formatMoney(costPerEngagement);
  const engagementMetric = formatPercent(
    hasSubmissions ? engagementRate : null,
  );

  const moneyMax = Math.max(totalSales, totalSpent);
  // The CPE scale is the benchmark itself, so the bar reads as "how much of
  // paid social's cost the campaign spends"; a dearer campaign simply fills it.
  // Engagement has no such ceiling, so it scales to whichever side is higher.
  const engagementMax = Math.max(engagementRate, metaAdsEngagementRate);

  return (
    <div
      data-testid="campaign-kpi-cards"
      className="flex w-full flex-col items-stretch gap-4 lg:flex-row lg:items-start"
    >
      <KpiCard
        testId="kpi-card-sales"
        label={t('common.salesGenerated')}
        metric={salesMetric}
        subtext={String(
          t('common.returnOnSpend', {
            roas: roas.toFixed(1),
            spent: formatMoney(totalSpent),
          }),
        )}
      >
        <LegendRow
          label={t('common.salesGenerated')}
          value={formatMoney(totalSales)}
          percent={barPercent(totalSales, moneyMax)}
          tone="campaign"
        />
        <LegendRow
          label={t('common.rewardedAmount')}
          value={formatMoney(totalSpent)}
          percent={barPercent(totalSpent, moneyMax)}
          tone="reward"
        />
      </KpiCard>

      <KpiCard
        testId="kpi-card-cpe"
        label={t('common.costPerEngagement')}
        labelTooltip={String(
          t('common.costPerEngagementTooltip', {
            spent: formatMoney(totalSpent),
            engagements: formatCompactNumber(totalEngagements),
          }),
        )}
        metric={cpeMetric}
        badge={
          costPerEngagement === null
            ? String(t('common.noEngagementYet'))
            : cheaperPercent !== null
              ? String(t('common.percentCheaper', { percent: cheaperPercent }))
              : undefined
        }
        badgeTone={cheaperPercent !== null ? 'positive' : 'neutral'}
        subtext={String(t('common.everyPostStartedWithARealPurchase'))}
      >
        <LegendRow
          label={t('common.yourLinkfluencerCampaign')}
          value={cpeMetric}
          percent={barPercent(costPerEngagement ?? 0, paidSocialCpe)}
          tone="campaign"
        />
        <LegendRow
          label={t('common.paidSocialBenchmarkBeauty')}
          value={formatMoney(paidSocialCpe)}
          percent={100}
          tone="benchmark"
        />
      </KpiCard>

      <KpiCard
        testId="kpi-card-engagement"
        label={t('common.engagementRate')}
        metric={engagementMetric}
        subtext={String(t('common.againstTheBeautyMedian'))}
        subtextClassName="!text-xs"
      >
        <LegendRow
          label={t('common.linkfluencerCampaign')}
          value={engagementMetric}
          percent={barPercent(
            hasSubmissions ? engagementRate : 0,
            engagementMax,
          )}
          tone="campaign"
        />
        <LegendRow
          label={t('common.metaAdsBenchmark')}
          value={formatPercent(metaAdsEngagementRate)}
          percent={barPercent(metaAdsEngagementRate, engagementMax)}
          tone="benchmark"
        />
      </KpiCard>
    </div>
  );
};

export default CampaignKpiCards;
